import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button, Icon } from '@advanza/ui'
import clsx from 'clsx'
import Card from 'components/Card'
import { useEffect, useState } from 'react'

interface Summary {
    total_messages_sent: number
    total_messages_delivered: number
    total_messages_read: number
    total_replies_received: number
    total_incoming: number
    overall_delivery_rate: number
    overall_read_rate: number
    overall_reply_rate: number
    avg_time_to_read_seconds: number | null
    avg_time_to_reply_seconds: number | null
}

interface ProviderMetrics {
    messages_sent: number
    messages_delivered: number
    messages_read: number
    delivery_rate: number
    read_rate: number
    replies_received: number
    reply_rate: number
    avg_time_to_read_seconds: number | null
    avg_time_to_reply_seconds: number | null
}

interface OutlierStats {
    total_read_time_points: number
    included_read_time_points: number
    excluded_read_time_points: number
    total_reply_time_points: number
    included_reply_time_points: number
    excluded_reply_time_points: number
}

interface Report {
    date_range: { start: string; end: string } | null
    outlier_method: OutlierMethod
    outlier_stats: OutlierStats
    summary: Summary
    provider_labels: Record<string, string>
    provider_metrics: Record<string, ProviderMetrics>
    status_breakdown_outgoing: Record<string, number>
    status_breakdown_incoming: Record<string, number>
    status_breakdown_combined: Record<string, number>
    template_breakdown_outgoing: Record<string, number>
    template_breakdown_incoming: Record<string, number>
    template_breakdown_combined: Record<string, number>
}

type OutlierMethod = 'none' | 'trimmed_mean' | 'iqr' | 'hard_threshold'

interface OutlierOption {
    value: OutlierMethod
    label: string
    description: string
}

const OUTLIER_OPTIONS: OutlierOption[] = [
    {
        value: 'none',
        label: 'No filtering',
        description: 'Show raw averages without any outlier exclusion.',
    },
    {
        value: 'trimmed_mean',
        label: 'Trimmed Mean (5%)',
        description:
            'Cuts the top and bottom 5% of extreme values before averaging. Gives a stable result that is not pulled by a few unusually fast or slow responses.',
    },
    {
        value: 'iqr',
        label: 'IQR Filter',
        description:
            'Detects outliers using the statistical spread of the data. Values beyond 1.5\u00d7 the interquartile range are excluded \u2014 a data-driven approach with no arbitrary cutoffs.',
    },
    {
        value: 'hard_threshold',
        label: '24-Hour Threshold',
        description:
            'Excludes any read or reply time longer than 24 hours. Assumes that responses after a full day are not representative of normal engagement.',
    },
]

const formatSeconds = (s: number | null): string => {
    if (s === null) return '–'

    const total = Math.round(s)
    if (total < 60) return `${total}s`

    const days = Math.floor(total / 86400)
    const hours = Math.floor((total % 86400) / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60

    if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`
    if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
}

type BreakdownTab = 'outgoing' | 'incoming' | 'combined'

const PocWhatsappAnalyticsMisc = () => {
    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')
    const [outlierMethod, setOutlierMethod] = useState<OutlierMethod>('none')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [report, setReport] = useState<Report | null>(null)
    const [breakdownTab, setBreakdownTab] = useState<BreakdownTab>('outgoing')

    const loadReport = async () => {
        setLoading(true)
        setError('')
        setReport(null)
        try {
            const params = new URLSearchParams()
            if (start) params.set('start', start)
            if (end) params.set('end', end)
            if (outlierMethod !== 'none') params.set('outlier_method', outlierMethod)
            const query = params.toString()
            const url = query
                ? `office/misc/poc-whatsapp-analytics?${query}`
                : 'office/misc/poc-whatsapp-analytics'
            const response = await call(url)
            if (response?.status === 'success' && response?.summary) {
                setReport(response as unknown as Report)
            } else {
                setError(response?.error || 'Failed to load report')
            }
        } catch (err) {
            setError('Error: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadReport()
    }, [outlierMethod])

    const renderStatusBlock = (title: string, breakdown: Record<string, number>) => (
        <div>
            <h4 className="text-md font-semibold text-neutral-700 mb-2">{title}</h4>
            <div className="flex flex-wrap gap-2">
                {Object.entries(breakdown).map(([key, count]) => (
                    <span
                        key={key}
                        className="inline-flex items-center rounded-md px-3 py-1.5 text-md font-medium bg-neutral-100 text-neutral-800 ring-1 ring-inset ring-neutral-200/50">
                        <span className="capitalize">{key}</span>
                        <span className="ml-1.5 font-bold tabular-nums">{count}</span>
                    </span>
                ))}
            </div>
        </div>
    )

    const renderTemplateBlock = (title: string, breakdown: Record<string, number>) => (
        <div>
            <h4 className="text-md font-semibold text-neutral-700 mb-2">{title}</h4>
            <div className="flex flex-wrap gap-2">
                {Object.entries(breakdown).map(([name, count]) => (
                    <span
                        key={name}
                        className="inline-flex items-center rounded-md px-3 py-1.5 text-md font-medium bg-neutral-100 text-neutral-800 ring-1 ring-inset ring-neutral-200/50">
                        <span className="truncate max-w-[200px]">{name}</span>
                        <span className="ml-1.5 font-bold tabular-nums">{count}</span>
                    </span>
                ))}
            </div>
        </div>
    )

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
                <InputField
                    type="date"
                    placeholder="Start (optional)"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                />
                <InputField
                    type="date"
                    placeholder="End (optional)"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                />
                <Button onClick={loadReport} disabled={loading} before={<Icon name="refresh" />}>
                    {loading ? 'Loading…' : 'Load / Refresh'}
                </Button>
            </div>

            <div className="space-y-2">
                <p className="text-md font-medium text-neutral-700">
                    Outlier exclusion{' '}
                    <span className="font-normal text-neutral-400">
                        (affects time-to-read &amp; time-to-reply averages)
                    </span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {OUTLIER_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            aria-pressed={outlierMethod === opt.value}
                            onClick={() => setOutlierMethod(opt.value)}
                            className={clsx(
                                'text-left rounded-lg border p-3 transition-colors',
                                outlierMethod === opt.value
                                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                    : 'border-neutral-200 bg-white hover:border-neutral-300',
                            )}>
                            <div className="text-md font-semibold text-neutral-900">
                                {opt.label}
                            </div>
                            <div className="mt-1 text-sm text-neutral-500 leading-relaxed">
                                {opt.description}
                            </div>
                        </button>
                    ))}
                </div>
                {report && outlierMethod !== 'none' && report.outlier_stats && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-md text-amber-800">
                        Excluded <strong>{report.outlier_stats.excluded_read_time_points}</strong>{' '}
                        of {report.outlier_stats.total_read_time_points} read-time data points and{' '}
                        <strong>{report.outlier_stats.excluded_reply_time_points}</strong> of{' '}
                        {report.outlier_stats.total_reply_time_points} reply-time data points.
                    </div>
                )}
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-md">
                    {error}
                </div>
            )}

            {loading && !report && (
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    <p className="mt-3 text-md text-neutral-500">Loading POC WhatsApp analytics…</p>
                </div>
            )}

            {report && (
                <div className="space-y-5">
                    {report.date_range && (
                        <p className="text-md text-neutral-500">
                            Date range:{' '}
                            <span className="font-medium text-neutral-700">
                                {report.date_range.start}
                            </span>
                            {' → '}
                            <span className="font-medium text-neutral-700">
                                {report.date_range.end}
                            </span>
                        </p>
                    )}

                    <Card useToggle header="Summary (KPIs)">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-blue-600 tabular-nums">
                                    {report.summary.total_messages_sent}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Sent
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-emerald-600 tabular-nums">
                                    {report.summary.total_messages_delivered}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Delivered
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-teal-600 tabular-nums">
                                    {report.summary.total_messages_read}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Read
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-violet-600 tabular-nums">
                                    {report.summary.total_replies_received}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Replies
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-display-xs font-bold text-slate-700 tabular-nums">
                                    {report.summary.overall_delivery_rate}%
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Delivery rate
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-display-xs font-bold text-slate-700 tabular-nums">
                                    {report.summary.overall_read_rate}%
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Read rate
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-display-xs font-bold text-slate-700 tabular-nums">
                                    {report.summary.overall_reply_rate}%
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Reply rate
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-xl font-bold text-slate-700">
                                    {formatSeconds(report.summary.avg_time_to_read_seconds)}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Avg time to read
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-xl font-bold text-slate-700">
                                    {formatSeconds(report.summary.avg_time_to_reply_seconds)}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Avg time to reply
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card useToggle header="Per provider">
                        <div className="overflow-x-auto rounded-lg border border-neutral-200">
                            <table className="min-w-full divide-y divide-neutral-200 text-md">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left font-semibold text-neutral-700">
                                            Provider
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Sent
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Delivered
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Read
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Replies
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Delivery %
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Read %
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Reply %
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Avg read
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-right font-semibold text-neutral-700">
                                            Avg reply
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-200 bg-white">
                                    {Object.entries(report.provider_metrics).map(
                                        ([providerId, m]) => (
                                            <tr key={providerId} className="hover:bg-neutral-50/80">
                                                <td className="px-4 py-3 font-medium text-neutral-900 whitespace-nowrap">
                                                    {report.provider_labels[providerId] ??
                                                        `Provider ${providerId}`}
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                                    {m.messages_sent}
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                                    {m.messages_delivered}
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                                    {m.messages_read}
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                                    {m.replies_received}
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                                    {m.delivery_rate}%
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                                    {m.read_rate}%
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                                    {m.reply_rate}%
                                                </td>
                                                <td className="px-4 py-3 text-right text-neutral-700">
                                                    {formatSeconds(m.avg_time_to_read_seconds)}
                                                </td>
                                                <td className="px-4 py-3 text-right text-neutral-700">
                                                    {formatSeconds(m.avg_time_to_reply_seconds)}
                                                </td>
                                            </tr>
                                        ),
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    <Card useToggle header="Status & template breakdown">
                        <div className="space-y-5">
                            <div className="flex rounded-lg bg-neutral-100 p-1 gap-1">
                                {(['outgoing', 'incoming', 'combined'] as const).map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setBreakdownTab(tab)}
                                        className={clsx(
                                            'flex-1 rounded-md px-4 py-2 text-md font-medium transition-colors capitalize',
                                            breakdownTab === tab
                                                ? 'bg-white text-neutral-900 shadow-xs ring-1 ring-neutral-200'
                                                : 'text-neutral-600 hover:text-neutral-900',
                                        )}>
                                        {tab}
                                    </button>
                                ))}
                            </div>

                            {breakdownTab === 'outgoing' && (
                                <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
                                    <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                        {renderStatusBlock(
                                            'Status (outgoing)',
                                            report.status_breakdown_outgoing,
                                        )}
                                    </div>
                                    <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                        {renderTemplateBlock(
                                            'Template (outgoing)',
                                            report.template_breakdown_outgoing,
                                        )}
                                    </div>
                                </div>
                            )}

                            {breakdownTab === 'incoming' && (
                                <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
                                    <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                        {renderStatusBlock(
                                            'Received (incoming)',
                                            report.status_breakdown_incoming,
                                        )}
                                    </div>
                                    <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                        {renderTemplateBlock(
                                            'Replied to template (incoming)',
                                            report.template_breakdown_incoming,
                                        )}
                                    </div>
                                </div>
                            )}

                            {breakdownTab === 'combined' && (
                                <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
                                    <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                        {renderStatusBlock(
                                            'Status (outgoing + incoming)',
                                            report.status_breakdown_combined,
                                        )}
                                    </div>
                                    <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                        {renderTemplateBlock(
                                            'Template (sent + replied)',
                                            report.template_breakdown_combined,
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}

export default PocWhatsappAnalyticsMisc
